import React, { useState } from 'react';
import { Modal, Button, Input, Icon, Message } from 'semantic-ui-react';
import cleanupCronData from '../../services/cleanupCronData';
import './CleanupModal.scss';

const CleanupModal = ({ isOpen, onClose, adminId, onSuccess }) => {
    const [retentionMonths, setRetentionMonths] = useState(6);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleCleanup = async () => {
        if (!retentionMonths || retentionMonths < 1) {
            setError('Please enter a valid number of months (minimum 1)');
            return;
        }

        // Confirmation alert
        const confirmed = window.confirm(
            `Are you sure you want to delete all data older than ${retentionMonths} month${retentionMonths !== 1 ? 's' : ''}?\n\n` +
            `This will permanently remove:\n` +
            `• Cron job history\n` +
            `• Cron job logs\n` +
            `• Cron job metrics\n\n` +
            `This action CANNOT be undone!`
        );

        if (!confirmed) {
            return;
        }

        try {
            setIsLoading(true);
            setError(null);
            
            await cleanupCronData(adminId, retentionMonths);
            
            if (onSuccess) {
                onSuccess();
            }
            
            onClose();
        } catch (err) {
            console.error('Error cleaning up cron data:', err);
            setError(
                (err.response && err.response.data && err.response.data.message) || 
                'Failed to cleanup data. Please try again.'
            );
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = () => {
        if (!isLoading) {
            setError(null);
            setRetentionMonths(6);
            onClose();
        }
    };

    return (
        <Modal
            open={isOpen}
            onClose={handleClose}
            size="small"
            className="cleanup-modal"
        >
            <Modal.Header>
                <Icon name="trash alternate" />
                Cleanup Old Cron Data
            </Modal.Header>
            <Modal.Content>
                <div className="cleanup-content">
                    <p className="cleanup-description">
                        This will permanently delete cron job history, logs, and metrics older than the specified retention period from AWS S3.
                    </p>
                    
                    <div className="retention-input-wrapper">
                        <label htmlFor="retention-months">
                            Retention Period (Months)
                        </label>
                        <Input
                            id="retention-months"
                            type="number"
                            min="1"
                            value={retentionMonths}
                            onChange={(e) => setRetentionMonths(parseInt(e.target.value) || '')}
                            placeholder="Enter number of months to retain"
                            disabled={isLoading}
                            fluid
                        />
                        <small className="helper-text">
                            Data older than {retentionMonths} month{retentionMonths !== 1 ? 's' : ''} will be deleted
                        </small>
                    </div>

                    {error && (
                        <Message negative>
                            <Icon name="warning circle" />
                            {error}
                        </Message>
                    )}

                    <Message warning>
                        <Icon name="warning sign" />
                        <strong>Warning:</strong> This action cannot be undone. Please make sure you have backups if needed.
                    </Message>
                </div>
            </Modal.Content>
            <Modal.Actions>
                <Button
                    onClick={handleClose}
                    disabled={isLoading}
                >
                    Cancel
                </Button>
                <Button
                    negative
                    onClick={handleCleanup}
                    loading={isLoading}
                    disabled={isLoading || !retentionMonths || retentionMonths < 1}
                >
                    <Icon name="trash" />
                    Cleanup Data
                </Button>
            </Modal.Actions>
        </Modal>
    );
};

export default CleanupModal;
